import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {IdentificationIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  IdentificationIcon as IdentificationIconSolid,
  InformationCircleIcon,
  PencilIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
  XCircleIcon
} from "@heroicons/react/solid";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {ContactMember, Membership, UserMember} from "types/Membership";
import Dropdown from "components/Dropdown";
import DeleteMembership from '../Delete';
import DotBadge from "components/DotBadge";
import MembershipStatus from "types/MembershipStatus";
import TicketingSource from "types/TicketingSource";
import SearchBar from "../../../components/SearchBar";
import {SortDirection} from "../../../types/SortDirection";
import useSorting from "../../../hooks/useSorting";
import {useOperator} from "../../../context/OperatorContext";
import Link from "../../../components/Link";

// Helpers ----

// Map message status to badge color
const statusColors: Record<MembershipStatus, number> = {
  [MembershipStatus.UNPAID]: DotBadge.colors.YELLOW,
  [MembershipStatus.PAID]: DotBadge.colors.BLUE,
  [MembershipStatus.VALID]: DotBadge.colors.GREEN,
  [MembershipStatus.EXPIRED]: DotBadge.colors.GRAY,
  [MembershipStatus.REVOKED]: DotBadge.colors.RED,
}

function memberNames(members: Array<UserMember | ContactMember>): string {
  const memberCount = members.length

  if (memberCount < 3) {
    return members.map(member => member.name).join(' and ')
  }

  const lastMember = members.pop()

  if (lastMember && memberCount < 4) {
    return members.map(member => member.name).join(', ') + ` and ${lastMember.name}`
  }

  return members.slice(0, 2).map(member => member.name).join(', ') + ` and ${memberCount - 2} others`
}

// Component ----

function MembershipList() {
  const {termFor} = useOperator();
  const {currentSort, queryString: sort, sortBy} = useSorting();
  const [search, setSearch] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const {data, isFetching, isLoading} = useQuery(
    ['memberships.all', {search, sort, page, perPage: 25}],
    () => ngage.memberships.filtered({search, sort, page, perPage: 25}),
    {keepPreviousData: true}
  )

  const [deletingMembership, setDeletingMembership] = useState<Membership | null>(null);

  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Prefetch next and previous pages
  prefetch('memberships.all', {search, sort, page, perPage: 25}, ngage.memberships.filtered)

  // Prepare meta data
  const counts = data?.meta?.counts ?? {};
  const meta = [
    {icon: IdentificationIconSolid, value: (counts.all ?? 0).toLocaleString() + ' issued'},
    {icon: QuestionMarkCircleIcon, value: (counts.unpaid ?? 0).toLocaleString() + ' unpaid'},
    {icon: InformationCircleIcon, value: (counts.paid ?? 0).toLocaleString() + ' paid'},
    {icon: CheckCircleIcon, value: (counts.valid ?? 0).toLocaleString() + ' valid'},
    {icon: XCircleIcon, value: (counts.expired ?? 0).toLocaleString() + ' expired'},
    {icon: ExclamationCircleIcon, value: (counts.revoked ?? 0).toLocaleString() + ' revoked'},
  ]

  const breadcrumbs = {'Commercial': '/', [termFor('Memberships')]: '/memberships'}
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/memberships/new')}>New</Button>

  return (
    <Page title={termFor('Memberships')} meta={meta} actions={actions} breadcrumbs={breadcrumbs}>
      {/* Search and filtering */}
      <div className="mb-4 w-full grid md:grid-cols-4">
        <SearchBar isSearching={isFetching} onChange={setSearch}/>
      </div>

      <Table paginationData={data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader
              sortable
              sortDirection={currentSort.number ?? SortDirection.NONE}
              onSort={direction => sortBy('number', direction)}>
              Number
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.reference ?? SortDirection.NONE}
              onSort={direction => sortBy('reference', direction)}>
              Reference
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.owner ?? SortDirection.NONE}
              onSort={direction => sortBy('owner', direction)}>
              Owner
            </TableHeader>
            <TableHeader>Member(s)</TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.plan ?? SortDirection.NONE}
              onSort={direction => sortBy('plan', direction)}>
              {termFor('Membership Plan')}
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.status ?? SortDirection.NONE}
              onSort={direction => sortBy('status', direction)}>
              Status
            </TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={isLoading}>

          {data && data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={7}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <IdentificationIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No memberships</span>
                </div>
              </TableData>
            </TableRow>
            : data?.data.map((membership, index) => (
              <TableRow key={membership.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position="left">
                        {[
                          {name: 'View', icon: EyeIcon, onClick: () => navigate(`/memberships/${membership.id}`)},
                          {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/memberships/${membership.id}/edit`)},
                          {
                            name: 'Delete',
                            icon: TrashIcon,
                            onClick: () => setDeletingMembership(membership),
                            disabled: (membership.source.name !== TicketingSource.NGAGE) || (membership.status === MembershipStatus.VALID)
                          }
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <IdentificationIcon className="h-5 w-5 mr-2 text-gray-500"/> {membership.number}
                    </div>
                  </div>
                </TableData>
                <TableData>{membership.reference ?? 'N/A'}</TableData>
                <TableData>
                  {membership.owner ?
                    <Link to={`/audience/${membership.owner.id}`}>
                      {membership.owner?.name}
                    </Link> :
                    'Unknown'
                  }
                </TableData>
                <TableData>{memberNames(membership.members)}</TableData>
                <TableData>{membership.plan.name}</TableData>
                <TableData>
                  <DotBadge color={statusColors[membership.status]} text={membership.status}/>
                </TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      {name: 'View', icon: EyeIcon, onClick: () => navigate(`/memberships/${membership.id}`)},
                      {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/memberships/${membership.id}/edit`)},
                      {
                        name: 'Delete',
                        icon: TrashIcon,
                        onClick: () => setDeletingMembership(membership),
                        disabled: (membership.source.name !== TicketingSource.NGAGE) || (membership.status === MembershipStatus.VALID)
                      }
                    ]}
                  </Dropdown>
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteMembership
                  membership={deletingMembership}
                  show={membership === deletingMembership}
                  onCancel={() => setDeletingMembership(null)}
                  onDelete={membership => {
                    navigate('/memberships')
                    notify("success", "Membership Deleted", `${membership.number} has been permanently removed`)
                  }}
                  onDeleteFail={membership => {
                    setDeletingMembership(null)
                    notify("error", "Membership Not Deleted", `${membership.number} could not be removed`)
                  }}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

// Export ----

export default MembershipList;